import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const CATEGORIES = ["All", "Awards", "Certificates", "Events", "Team", "Videos"];

const GALLERY = [
  {
    src: "/gallery/business-catalyst-customer-management-fy-2023-24.jpg",
    title: "Business Catalyst Customer Management FY 2023-24",
    category: "Events",
    alt: "Prime Technology at Business Catalyst Customer Management event FY 2023-24",
    span: "col-span-2 row-span-2",
  },
  {
    src: "/gallery/retention-excellence-award-trophy-certificate.jpg",
    title: "Retention Excellence Award Trophy & Certificate",
    category: "Awards",
    alt: "TallyPrime Retention Excellence Award trophy and certificate for Prime Technology H1 FY 2024-25",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/tallyprime-retention-excellence-award-h1-fy-2024-25.jpg",
    title: "Retention Excellence Award H1 FY 2024-25",
    category: "Awards",
    alt: "TallyPrime Retention Excellence Award trophy with silver star for Prime Technology",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/retention-excellence-award-ceremony-prime-technology.jpg",
    title: "Retention Excellence Award Ceremony",
    category: "Events",
    alt: "Award ceremony for Retention Excellence H1 2024-25 presented to Prime Technology",
    span: "col-span-1 row-span-2",
  },
  {
    src: "/gallery/retention-excellence-award-trophy-closeup.jpg",
    title: "Retention Excellence Award Trophy Close-up",
    category: "Awards",
    alt: "Close-up of TallyPrime Retention Excellence Award plaque for Prime Technology H1 FY 2024-25",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/certificate-appreciation-tallyprime-customer-retention.jpg",
    title: "Certificate of Appreciation - Customer Retention",
    category: "Certificates",
    alt: "TallyPrime Certificate of Appreciation for exceptional customer retention performance H1 FY 2024-25",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/tally-power-of-simplicity-team-event.jpg",
    title: "Tally Power of Simplicity Team Event",
    category: "Team",
    alt: "Prime Technology team at Tally Power of Simplicity branded event",
    span: "col-span-2 row-span-1",
  },
  {
    src: "/gallery/tally-3-star-certified-partners-meet-up-east-2025-26.jpg",
    title: "3-Star Certified Partners Meet UP East FY 2025-26",
    category: "Events",
    alt: "Tally 3-Star Certified Partners Meet UP East FY 2025-26 award ceremony on stage",
    span: "col-span-2 row-span-1",
  },
  {
    src: "/gallery/customer-connect-champ-award-ceremony-fy-2025-26.jpg",
    title: "Customer Connect Champ Award FY 2025-26",
    category: "Awards",
    alt: "EEE Customer Connect Champion award ceremony at 3-Star Certified Partners Meet UP East",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/certificate-of-honor-certified-partner-day-2025.jpg",
    title: "Certificate of Honor - Certified Partner Day 2025",
    category: "Certificates",
    alt: "Tally Certificate of Honor for Prime Technology on Certified Partner Day 19 November 2025",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/tallyprime-partner-meeting-prime-technology.jpg",
    title: "TallyPrime Partner Meeting - Prime Technology",
    category: "Team",
    alt: "Prime Technology representative at TallyPrime certified partner meeting",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/prime-technology-certified-partner-conference.jpg",
    title: "Prime Technology at Certified Partner Conference",
    category: "Team",
    alt: "Prime Technology team member at Tally certified partner conference event",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/tally-3-star-sales-implementation-partner-certificate.jpg",
    title: "3-Star Sales & Implementation Partner Certificate",
    category: "Certificates",
    alt: "Official Tally 3-Star Sales and Implementation Partner certificate for Prime Technology",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/tally-welcome-kit-partnership-ceremony.jpg",
    title: "Tally Welcome Kit Partnership Ceremony",
    category: "Events",
    alt: "Tally partnership welcome kit handover ceremony with certificate",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/certificate-appreciation-framed-wall.jpg",
    title: "Certificate of Appreciation - Prime Technology",
    category: "Certificates",
    alt: "Framed TallyPrime Certificate of Appreciation for Prime Technology customer retention H1 FY 2024-25",
    span: "col-span-1 row-span-1",
  },
  // VIDEOS — add your .mp4 files to /public/gallery/ and update src/thumb below
  {
    type: "video",
    src: "/gallery/prime-technology-awards-highlights.mp4",
    thumb: "/gallery/business-catalyst-customer-management-fy-2023-24.jpg",
    title: "Prime Technology Awards Highlights 2024-25",
    category: "Videos",
    alt: "Prime Technology TallyPrime awards and achievements highlights video",
    span: "col-span-2 row-span-1",
  },
];

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = activeCategory === "All" ? GALLERY : GALLERY.filter((g) => g.category === activeCategory);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const goPrev = () => setLightbox((i) => (i - 1 + filtered.length) % filtered.length);
  const goNext = () => setLightbox((i) => (i + 1) % filtered.length);

  const currentItem = lightbox !== null ? filtered[lightbox] : null;

  return (
    <div className="relative bg-[#FAF9F6]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#0D47A1] text-white overflow-hidden circuit-pattern">
        <div className="orb bg-[#FFA000] w-[300px] h-[300px] -top-20 -right-20 opacity-15" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" animate="show">
            <motion.h1 variants={fadeUp} className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Awards & <span className="text-[#FFA000]">Gallery</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-4 text-lg text-white/75 max-w-xl">
              Celebrating excellence — our TallyPrime awards, certifications, events and team moments that define Prime Technology's journey.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Filter + Gallery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* SEO heading */}
          <div className="mb-8 max-w-2xl">
            <h2 className="font-display font-bold text-2xl text-[#1A1A2E]">
              TallyPrime Awards & Certifications Gallery
            </h2>
            <p className="mt-2 text-[15px] text-[#4A5568] leading-relaxed">
              Prime Technology is a Tally 3-Star Certified Sales & Implementation Partner, recognized for Retention Excellence, Customer Connect Champion and consistent business performance across multiple fiscal years.
            </p>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-10">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-[#0D47A1] text-white shadow-[0_8px_20px_rgba(13,71,161,0.25)]"
                    : "bg-white text-[#4A5568] border border-[#0D47A1]/10 hover:border-[#0D47A1]/30"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Masonry-style grid */}
          <motion.div layout className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
            <AnimatePresence mode="popLayout">
              {filtered.map((item, i) => (
                <motion.div
                  key={item.src}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  onClick={() => openLightbox(i)}
                  className={`${item.span} relative rounded-2xl overflow-hidden cursor-pointer group`}
                >
                  <img
                    src={item.type === "video" ? item.thumb : item.src}
                    alt={item.alt}
                    title={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  {/* Play icon overlay for videos */}
                  {item.type === "video" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors duration-300">
                      <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-[0_8px_30px_rgba(0,0,0,0.3)] group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-6 h-6 text-[#0D47A1] ml-1" fill="#0D47A1" />
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D47A1]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <h4 className="font-display font-bold text-white text-sm leading-tight">{item.title}</h4>
                    <span className="text-xs text-white/70 uppercase tracking-wider">{item.category}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && currentItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6"
            onClick={closeLightbox}
          >
            <button onClick={closeLightbox} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition">
              <X className="w-5 h-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-4 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-4 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center max-w-full" onClick={(e) => e.stopPropagation()}>
              {currentItem.type === "video" ? (
                <motion.video
                  key={currentItem.src}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  src={currentItem.src}
                  title={currentItem.title}
                  controls
                  autoPlay
                  className="max-w-full max-h-[75vh] rounded-2xl"
                />
              ) : (
                <motion.img
                  key={currentItem.src}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  src={currentItem.src}
                  alt={currentItem.alt}
                  title={currentItem.title}
                  className="max-w-full max-h-[75vh] rounded-2xl object-contain"
                />
              )}
              <div className="mt-4 text-center max-w-lg">
                <h4 className="font-display font-bold text-white text-lg">{currentItem.title}</h4>
                <span className="text-sm text-white/60">{currentItem.category} | Prime Technology - TallyPrime Certified Partner</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
